'use client';

import { useState } from 'react';
import { supabase } from '@/lib/supabaseClient';

import { useRouter } from 'next/navigation';

interface FormData {
  fullName: string;
  email: string;
  phone: string;
  preferredMarkets: string;
  propertyType: string;
  priceRange: string;
  strategy: string;
  notes: string;
}

const emptyToNull = (value: string) => {
  const trimmed = value.trim();
  return trimmed.length ? trimmed : null;
};

const ApplicationForm = () => {
  const router = useRouter();
  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    email: '',
    phone: '',
    preferredMarkets: '',
    propertyType: '',
    priceRange: '',
    strategy: '',
    notes: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const { error: submitError } = await supabase.from('buyer_leads').insert({
        full_name: formData.fullName.trim(),
        email: formData.email.trim(),
        phone: emptyToNull(formData.phone),
        preferred_markets: emptyToNull(formData.preferredMarkets),
        property_type: emptyToNull(formData.propertyType),
        price_range: emptyToNull(formData.priceRange),
        strategy: emptyToNull(formData.strategy),
        notes: emptyToNull(formData.notes),
      });

      if (submitError) {
        throw submitError;
      }

      // Show success message instead of redirecting
      setSubmitted(true);
    } catch (err: any) {
      console.error('Error submitting form:', err);
      setError(err.message || 'Failed to submit form. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fieldClass =
    'w-full border-0 border-b border-border bg-transparent px-0 py-2.5 text-[15px] text-foreground outline-none transition-colors focus:border-primary focus:ring-0';
  const labelClass =
    'block text-[11px] font-bold uppercase tracking-[0.22em] text-muted-foreground';

  return (
    <section id="application" className="min-h-screen bg-background py-16 lg:py-24">
      <div className="mx-auto w-full max-w-7xl px-6 lg:px-8">
        {submitted ? (
          <div className="text-center">
            <div className="bg-card rounded-xl p-12 luxury-shadow">
              <div className="mb-6">
                <svg
                  className="w-20 h-20 text-primary mx-auto"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h2 className="text-3xl font-heading font-bold text-foreground mb-4">
                Thank you for submitting!
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                We've received your application and will be in touch soon with exclusive off-market
                deals.
              </p>
              <button
                onClick={() => router.push('/')}
                className="bg-primary text-white py-3 px-8 rounded-lg font-semibold hover:bg-primary/90 transition-colors"
              >
                Return to Home
              </button>
            </div>
          </div>
        ) : (
          <div className="grid items-start gap-16 lg:grid-cols-[1.05fr_0.95fr]">
            <div className="pt-6">
              <div className="mb-16 h-px w-20 bg-accent"></div>
              <div className="mb-7 font-heading text-xl italic text-primary">
                — For Select Buyers
              </div>
              <h1 className="max-w-[760px] text-[52px] font-normal leading-[0.95] text-foreground md:text-[70px] lg:text-[82px]">
                Private access
                <br />
                to off-market <span className="italic text-plum">deals.</span>
              </h1>
              <p className="mb-12 mt-7 max-w-[620px] font-heading text-[22px] leading-[1.55] text-muted-foreground">
                Join the buyers list for curated wholesale opportunities, clean numbers, and quiet
                deal flow matched to your investment criteria.
              </p>
              <div className="mb-10 flex items-center gap-4 text-accent">
                <span className="h-px w-10 bg-accent"></span>
                <span>✦</span>
                <span className="h-px w-10 bg-accent"></span>
              </div>
              <ul className="space-y-6">
                {[
                  ['Curated inventory.', 'Only opportunities that fit serious investor buy boxes.'],
                  ['Investor-ready numbers.', 'Price, ARV, repairs, and timing presented clearly.'],
                  ['Discreet deal flow.', 'Private opportunities before they reach wider channels.'],
                ].map(([strong, text]) => (
                  <li key={strong} className="flex items-start gap-7">
                    <span className="mt-1 text-xl text-accent">✦</span>
                    <span>
                      <strong className="font-bold text-plum">{strong}</strong> {text}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="relative border border-border bg-card/70 p-8 shadow-card md:p-11">
              <span className="absolute left-3 top-3 h-7 w-7 border-l border-t border-accent"></span>
              <span className="absolute bottom-3 right-3 h-7 w-7 border-b border-r border-accent"></span>
              <div className="mb-6 text-center text-[11px] font-bold uppercase tracking-[0.34em] text-accent">
                — Investor Application —
              </div>
              <h2 className="mb-2 text-center font-heading text-[34px] font-normal leading-tight md:text-[42px]">
                Join Our <span className="italic text-plum">Buyers List</span>
              </h2>
              <p className="mb-8 text-center font-heading text-base italic text-muted-foreground">
                Get notified when we have off-market deals.
              </p>
              <div className="mb-8 flex items-center gap-4 text-accent">
                <span className="h-px flex-1 bg-border"></span>
                <span>✦</span>
                <span className="h-px flex-1 bg-border"></span>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Full Name */}
                <div>
                  <label htmlFor="fullName" className={labelClass}>
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="fullName"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    required
                    className={fieldClass}
                  />
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className={labelClass}>
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className={fieldClass}
                  />
                </div>

                {/* Phone */}
                <div>
                  <label htmlFor="phone" className={labelClass}>
                    Phone
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className={fieldClass}
                  />
                </div>

                {/* Preferred Markets */}
                <div>
                  <label htmlFor="preferredMarkets" className={labelClass}>
                    Preferred Markets
                  </label>
                  <input
                    type="text"
                    id="preferredMarkets"
                    name="preferredMarkets"
                    value={formData.preferredMarkets}
                    onChange={handleChange}
                    placeholder="e.g., Phoenix, Las Vegas, Austin"
                    className={fieldClass}
                  />
                </div>

                {/* Property Type */}
                <div>
                  <label htmlFor="propertyType" className={labelClass}>
                    Property Type
                  </label>
                  <select
                    id="propertyType"
                    name="propertyType"
                    value={formData.propertyType}
                    onChange={handleChange}
                    className={fieldClass}
                  >
                    <option value="">Select Type</option>
                    <option value="Single Family Home">Single Family Home</option>
                    <option value="Multi-Family">Multi-Family</option>
                    <option value="Condo">Condo</option>
                    <option value="Townhouse">Townhouse</option>
                    <option value="Land">Land</option>
                    <option value="Commercial">Commercial</option>
                    <option value="Any Type">Any Type</option>
                  </select>
                </div>

                {/* Price Range */}
                <div>
                  <label htmlFor="priceRange" className={labelClass}>
                    Price Range
                  </label>
                  <select
                    id="priceRange"
                    name="priceRange"
                    value={formData.priceRange}
                    onChange={handleChange}
                    className={fieldClass}
                  >
                    <option value="">Select Range</option>
                    <option value="$0-$100k">$0-$100k</option>
                    <option value="$100k-$250k">$100k-$250k</option>
                    <option value="$250k-$500k">$250k-$500k</option>
                    <option value="$500k-$1M">$500k-$1M</option>
                    <option value="$1M+">$1M+</option>
                  </select>
                </div>

                {/* Strategy */}
                <div>
                  <label htmlFor="strategy" className={labelClass}>
                    Investment Strategy
                  </label>
                  <select
                    id="strategy"
                    name="strategy"
                    value={formData.strategy}
                    onChange={handleChange}
                    className={fieldClass}
                  >
                    <option value="">Select Strategy</option>
                    <option value="Fix & Flip">Fix & Flip</option>
                    <option value="Rental">Rental</option>
                    <option value="Wholesale">Wholesale</option>
                    <option value="Fix & Flip, Rental">Fix & Flip, Rental</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                {/* Notes */}
                <div>
                  <label htmlFor="notes" className={labelClass}>
                    Additional Notes
                  </label>
                  <textarea
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    rows={4}
                    placeholder="Tell us more about what you're looking for..."
                    className={fieldClass}
                  />
                </div>

                {/* Error Message */}
                {error && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-primary px-6 py-4 text-[12px] font-bold uppercase tracking-[0.34em] text-white transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {loading ? 'Submitting...' : 'Submit Application'}
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default ApplicationForm;
